// NEWS2 (National Early Warning Score 2) utility — range-table based to avoid nested ternaries.

const toNum = (x) => {
  const n = parseFloat(x);
  return isNaN(n) ? null : n;
};

// Each entry: [predicate(value) -> boolean, points]. First match wins.
const NEWS2_TABLES = {
  rr: [
    [(n) => n <= 8, 3], [(n) => n <= 11, 1], [(n) => n <= 20, 0], [(n) => n <= 24, 2], [() => true, 3],
  ],
  spo2: [
    [(n) => n >= 96, 0], [(n) => n >= 94, 1], [(n) => n >= 92, 2], [() => true, 3],
  ],
  temp: [
    [(n) => n <= 35, 3], [(n) => n <= 36, 1], [(n) => n <= 38, 0], [(n) => n <= 39, 1], [() => true, 2],
  ],
  sbp: [
    [(n) => n <= 90, 3], [(n) => n <= 100, 2], [(n) => n <= 110, 1], [(n) => n <= 219, 0], [() => true, 3],
  ],
  hr: [
    [(n) => n <= 40, 3], [(n) => n <= 50, 1], [(n) => n <= 90, 0], [(n) => n <= 110, 1], [(n) => n <= 130, 2], [() => true, 3],
  ],
};

function scoreParam(table, value) {
  for (const [match, points] of table) {
    if (match(value)) return points;
  }
  return 0;
}

export function computeNews2(vitals = {}) {
  const parts = [];
  for (const key of Object.keys(NEWS2_TABLES)) {
    const value = toNum(vitals[key]);
    if (value != null) parts.push(scoreParam(NEWS2_TABLES[key], value));
  }
  if (parts.length === 0) return null;

  const score = parts.reduce((a, b) => a + b, 0);
  const hasThree = parts.some((p) => p >= 3);

  if (score >= 7) return { score, band: "High — urgent / emergency clinical response", cls: "bg-red-600" };
  if (score >= 5 || hasThree) return { score, band: "Medium — urgent review required", cls: "bg-amber-500" };
  return { score, band: "Low — routine monitoring", cls: "bg-emerald-600" };
}

// ---------------------------------------------------------------------------
// Deterministic critical-care score helpers (GCS, qSOFA, SOFA, APACHE II,
// HEART, CURB-65). Pure functions — extracted from ClinicalScores to keep the
// component lean and the medical math independently testable.
// ---------------------------------------------------------------------------
export const num = (x) => { const n = parseFloat(x); return isNaN(n) ? null : n; };

export function sevCls(value, amberAt, redAt) {
  if (value >= redAt) return "bg-red-600";
  if (value >= amberAt) return "bg-amber-500";
  return "bg-emerald-600";
}

export function gcsSeverity(t) {
  if (t <= 8) return { label: "Severe (coma)", cls: "bg-red-600" };
  if (t <= 12) return { label: "Moderate", cls: "bg-amber-500" };
  return { label: "Mild / alert", cls: "bg-emerald-600" };
}

export function qsofaCls(total, high) {
  if (high) return "bg-red-600";
  if (total === 1) return "bg-amber-500";
  return "bg-emerald-600";
}

export function curbBand(total) {
  if (total <= 1) return { label: "Low severity — consider outpatient", cls: "bg-emerald-600" };
  if (total === 2) return { label: "Moderate — consider admission", cls: "bg-amber-500" };
  return { label: "Severe — admit / assess ICU", cls: "bg-red-600" };
}

// APACHE II
export function apTemp(t){ if(t==null)return 0; if(t>=41)return 4; if(t>=39)return 3; if(t>=38.5)return 1; if(t>=36)return 0; if(t>=34)return 1; if(t>=32)return 2; if(t>=30)return 3; return 4; }
export function apMap(m){ if(m==null)return 0; if(m>=160)return 4; if(m>=130)return 3; if(m>=110)return 2; if(m>=70)return 0; if(m>=50)return 2; return 4; }
export function apHr(h){ if(h==null)return 0; if(h>=180)return 4; if(h>=140)return 3; if(h>=110)return 2; if(h>=70)return 0; if(h>=55)return 2; if(h>=40)return 3; return 4; }
export function apRr(r){ if(r==null)return 0; if(r>=50)return 4; if(r>=35)return 3; if(r>=25)return 1; if(r>=12)return 0; if(r>=10)return 1; if(r>=6)return 2; return 4; }
export function apOxy(pao2,fio2){ const f=num(fio2),p=num(pao2); if(f!=null&&f>=0.5){ if(p==null)return 0; const a=f*713-40/0.8-p; if(a>=500)return 4; if(a>=350)return 3; if(a>=200)return 2; return 0; } if(p==null)return 0; if(p>70)return 0; if(p>=61)return 1; if(p>=55)return 3; return 4; }
export function apPh(ph){ if(ph==null)return 0; if(ph>=7.7)return 4; if(ph>=7.6)return 3; if(ph>=7.5)return 1; if(ph>=7.33)return 0; if(ph>=7.25)return 2; if(ph>=7.15)return 3; return 4; }
export function apNa(na){ if(na==null)return 0; if(na>=180)return 4; if(na>=160)return 3; if(na>=155)return 2; if(na>=150)return 1; if(na>=130)return 0; if(na>=120)return 2; if(na>=111)return 3; return 4; }
export function apK(k){ if(k==null)return 0; if(k>=7)return 4; if(k>=6)return 3; if(k>=5.5)return 1; if(k>=3.5)return 0; if(k>=3)return 1; if(k>=2.5)return 2; return 4; }
export function apCreat(c,arf){ if(c==null)return 0; let p; if(c>=3.5)p=4; else if(c>=2)p=3; else if(c>=1.5)p=2; else if(c>=0.6)p=0; else p=2; return arf?p*2:p; }
export function apHct(h){ if(h==null)return 0; if(h>=60)return 4; if(h>=50)return 2; if(h>=46)return 1; if(h>=30)return 0; if(h>=20)return 2; return 4; }
export function apWbc(w){ if(w==null)return 0; if(w>=40)return 4; if(w>=20)return 2; if(w>=15)return 1; if(w>=3)return 0; if(w>=1)return 2; return 4; }
export function apAge(a){ if(a==null)return 0; if(a>=75)return 6; if(a>=65)return 5; if(a>=55)return 3; if(a>=45)return 2; return 0; }
export function apMort(t){ if(t<=4)return "~4% mortality"; if(t<=9)return "~8%"; if(t<=14)return "~15%"; if(t<=19)return "~25%"; if(t<=24)return "~40%"; if(t<=29)return "~55%"; if(t<=34)return "~73%"; return "~85%"; }

// SOFA
export function sofaResp(ratio,support){ if(ratio==null)return 0; if(ratio>=400)return 0; if(ratio>=300)return 1; if(ratio>=200)return 2; if(ratio>=100)return support?3:2; return support?4:2; }
export function sofaPlt(p){ if(p==null)return 0; if(p>=150)return 0; if(p>=100)return 1; if(p>=50)return 2; if(p>=20)return 3; return 4; }
export function sofaBili(b){ if(b==null)return 0; if(b<1.2)return 0; if(b<2)return 1; if(b<6)return 2; if(b<12)return 3; return 4; }
export function sofaCns(g){ if(g>=15)return 0; if(g>=13)return 1; if(g>=10)return 2; if(g>=6)return 3; return 4; }
export function sofaRenal(c){ if(c==null)return 0; if(c<1.2)return 0; if(c<2)return 1; if(c<3.5)return 2; if(c<5)return 3; return 4; }

// HEART
export function heartAge(a){ if(a==null)return 0; if(a>=65)return 2; if(a>=45)return 1; return 0; }
export function heartBand(t){ if(t<=3)return {label:"Low risk (MACE ~1.7%)",cls:"bg-emerald-600"}; if(t<=6)return {label:"Moderate risk (~16.6%)",cls:"bg-amber-500"}; return {label:"High risk (~50%)",cls:"bg-red-600"}; }

// Aggregate: compute every critical-care score from the raw inputs. Pure — keeps
// the ClinicalScores component free of medical math.
function apacheScore({ ap, map, hr, rr, temp, gcsTotal, ageN }) {
  const apsPts = apTemp(temp) + apMap(map) + apHr(hr) + apRr(rr) + apOxy(ap.pao2, ap.fio2)
    + apPh(num(ap.ph)) + apNa(num(ap.na)) + apK(num(ap.k)) + apCreat(num(ap.creat), ap.arf)
    + apHct(num(ap.hct)) + apWbc(num(ap.wbc)) + (15 - gcsTotal);
  const total = apsPts + apAge(ageN) + Number(ap.chronic);
  const caption = `${apMort(total)} · APS ${apsPts} + age ${apAge(ageN)} + chronic ${ap.chronic}${map == null ? " · enter BP for MAP" : ""}`;
  return { total, caption };
}

function sofaScore(sf, gcsTotal) {
  const ratio = num(sf.pao2) != null && num(sf.fio2) ? Math.round(num(sf.pao2) / num(sf.fio2)) : null;
  return sofaResp(ratio, sf.vent) + sofaPlt(num(sf.plt)) + sofaBili(num(sf.bili))
    + Number(sf.cardio) + sofaCns(gcsTotal) + sofaRenal(num(sf.creat));
}

function wellsScore(wells, hrOver100) {
  return (wells.signsDvt ? 3 : 0) + (wells.peLikely ? 3 : 0) + (hrOver100 ? 1.5 : 0)
    + (wells.immob ? 1.5 : 0) + (wells.prevVte ? 1.5 : 0) + (wells.hemoptysis ? 1 : 0) + (wells.malignancy ? 1 : 0);
}

function curbScore({ curb, gcsTotal, rr, sbp, dbp, ageN }) {
  return (gcsTotal < 15 ? 1 : 0) + (num(curb.bun) != null && num(curb.bun) > 19 ? 1 : 0)
    + (rr != null && rr >= 30 ? 1 : 0) + ((sbp != null && sbp < 90) || (dbp != null && dbp <= 60) ? 1 : 0)
    + (ageN != null && ageN >= 65 ? 1 : 0);
}

function buildScoresPayload(r, flags) {
  const { pediatric, apacheOpen, sofaOpen, cardiacOpen, wellsOpen, curbOpen } = flags;
  const adult = (open, obj) => (!pediatric && open ? obj : null);
  return {
    gcs: { total: r.gcsTotal, severity: r.gsev.label, breakdown: r.gcsBreakdown },
    qsofa: pediatric ? null : { total: r.qTotal, high_risk: r.qHigh },
    apache2: adult(apacheOpen, { total: r.apacheTotal, predicted_mortality: apMort(r.apacheTotal) }),
    sofa: adult(sofaOpen, { total: r.sofaTotal }),
    heart: adult(cardiacOpen, { total: r.heartTotal, risk: r.hBand.label }),
    timi: adult(cardiacOpen, { total: r.timiTotal }),
    wells: adult(wellsOpen, { total: r.wellsTotal, likely: r.wellsLikely }),
    curb65: adult(curbOpen, { total: r.curbTotal, risk: r.curbBandVal.label }),
  };
}

export function computeScores(i) {
  const { vitals = {}, age, eye, verbal, motor, ap, sf, ht, timi, wells, curb } = i;
  const gcsTotal = eye + verbal + motor;
  const gsev = gcsSeverity(gcsTotal);
  const rr = num(vitals.rr), sbp = num(vitals.sbp), dbp = num(vitals.dbp), hr = num(vitals.hr), temp = num(vitals.temp);
  const map = sbp != null && dbp != null ? Math.round((sbp + 2 * dbp) / 3) : null;
  const ageN = num(age);

  const qTotal = (rr != null && rr >= 22 ? 1 : 0) + (sbp != null && sbp <= 100 ? 1 : 0) + (gcsTotal < 15 ? 1 : 0);
  const qHigh = qTotal >= 2;

  const heartAgePts = heartAge(ageN);
  const { total: apacheTotal, caption: apacheCaption } = apacheScore({ ap, map, hr, rr, temp, gcsTotal, ageN });
  const sofaTotal = sofaScore(sf, gcsTotal);

  const heartTotal = Number(ht.history) + Number(ht.ecg) + heartAgePts + Number(ht.risk) + Number(ht.trop);
  const hBand = heartBand(heartTotal);
  const timiTotal = Object.values(timi).filter(Boolean).length;

  const hrOver100 = hr != null && hr > 100;
  const wellsTotal = wellsScore(wells, hrOver100);
  const wellsLikely = wellsTotal > 4;

  const curbConfusion = gcsTotal < 15;
  const curbTotal = curbScore({ curb, gcsTotal, rr, sbp, dbp, ageN });
  const curbBandVal = curbBand(curbTotal);

  const r = {
    gcsTotal, gsev, gcsBreakdown: { eye, verbal, motor }, qTotal, qHigh,
    apacheTotal, apacheCaption, sofaTotal, heartTotal, hBand, timiTotal, heartAgePts,
    hrOver100, wellsTotal, wellsLikely, curbConfusion, curbTotal, curbBandVal,
  };
  const payload = buildScoresPayload(r, i);
  return { ...r, payload };
}

